import {
  type AgendaItem,
  type AgendaItemList,
  type Encounter,
  getGetEncounterByIdQueryKey,
  getListActionItemsForEncounterQueryKey,
  getListAgendaItemsQueryKey,
  getListMedicalCodesByEncounterQueryKey,
} from "@elation/api-client/src/stitch-web";
import {
  matchesActiveEncounter,
  useEncounterId,
  useIsEncounterCompleted,
  useIsEncounterFinalizing,
} from "@src/features/encounters/state/encounterActor";
import { useAppBusEvent } from "@src/features/sse";
import { useQueryClient } from "@tanstack/react-query";

/** Matches agenda-items query cache shape (see useAgendaItemMutations). */
type CachedListAgendaItems = { response?: unknown; data: AgendaItemList };
type CachedEncounter = { response?: unknown; data: Encounter };

/**
 * Keeps React Query encounter + agenda caches aligned with live data from
 * {@link useAppSSEEvents}, which forwards SSE frames onto `appBus`.
 *
 * Only applies updates when `payload.encounterId` matches the store's
 * current encounter ({@link matchesActiveEncounter}).
 */
export function useEncounter() {
  const queryClient = useQueryClient();

  const encounterId = useEncounterId();
  const isEncounterCompleted = useIsEncounterCompleted();
  const isEncounterFinalizing = useIsEncounterFinalizing();

  useAppBusEvent("encounter.snapshot", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({ ...old, data: { items: payload.items } }),
    );
  });

  useAppBusEvent("agenda_item.created", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => {
        const items: AgendaItem[] = old?.data?.items ?? [];
        if (items.some((i) => i.id === payload.item.id)) return old;
        return { ...old, data: { items: [...items, payload.item] } };
      },
    );
  });

  useAppBusEvent("agenda_item.updated", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          items: (old?.data?.items ?? []).map((i: AgendaItem) =>
            i.id === payload.item.id ? payload.item : i,
          ),
        },
      }),
    );
  });

  useAppBusEvent("agenda_item.deleted", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedListAgendaItems>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          items: (old?.data?.items ?? []).filter(
            (i: AgendaItem) => i.id !== payload.itemId,
          ),
        },
      }),
    );
  });

  useAppBusEvent("encounter.finalizing", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "finalizing" } }
          : old,
    );
  });

  useAppBusEvent("encounter.completed", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "completed" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListActionItemsForEncounterQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListMedicalCodesByEncounterQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.completed",
        { encounterId: payload.encounterId, err },
      ),
    );
  });

  useAppBusEvent("encounter.reopened", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "in_progress" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListAgendaItemsQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.reopened",
        { encounterId: payload.encounterId, err },
      ),
    );
  });

  return {
    encounterId,
    isEncounterCompleted,
    isEncounterFinalizing,
  };
}
